import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import { userStore } from '../../auth/store';

export interface HrWorkerPersonal {
  personId: string;
  autoIncrementIntellihrId: string | null;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
  employeeNumber: string | null;
  dateOfBirth: string | null;
  gender: string | null;
}

export interface HrWorkersPage {
  people: HrWorkerPersonal[];
  count: number;
  currentPage: number;
  perPage: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface WorkerEmployment {
  id: string;
  personId: string;
  businessRecordId: string;
  payGroupId: string;
}

export interface WorkerSuperannuation {
  id: string;
  personId: string;
  superSchemeId: string;
}

export interface WorkerAssignments {
  employment: WorkerEmployment | null;
  superannuation: WorkerSuperannuation | null;
}

export interface WorkerReference { id: string; name: string }

export interface WorkerPayrollDetails {
  personId: string;
  banking: { accounts: Array<{
    accountName: string | null; bsb: string | null; maskedAccountNumber: string | null;
    splitKind: string | null; splitValue: number | null; rank: number | null;
  }> } | null;
  tax: {
    hasFinancialSupplementDebt: boolean | null;
    hasStudyAndTrainingRepayment: boolean | null;
    payBasis: string | null;
    residencyType: string | null;
    taxFreeThresholdClaimed: boolean | null;
    tfnExemptionType: string | null;
    tfnSupplied: boolean | null;
    declaration: {
      state: string | null; title: string | null; addressLine1: string | null;
      addressLine2: string | null; locality: string | null; postcode: string | null;
      dateOfBirth: string | null; familyName: string | null; givenName: string | null;
      middleName: string | null; previousFamilyName: string | null; previousGivenName: string | null;
    };
    acceptance: { information: boolean | null; stapling: boolean | null; tfnd: boolean | null };
    origin: { name: string | null };
  } | null;
  superFund: {
    variant: 'product' | 'smsf' | 'ambiguous'; fundName: string | null; fundAbn: string | null;
    usi: string | null; esa: string | null; maskedMemberNumber: string | null;
  } | null;
}
export interface WorkerLeaveAccount {
  id: string; personId: string; workAssignmentId: string | null; personLeaveRuleAssignmentId: string; leaveRuleId: string;
  leaveTypeCode: string; leaveTypeName: string; ruleName: string; ruleCode: string; ruleVersion: number;
  ruleSource: string; assignmentStatus: string; assignmentEffectiveFrom: string; assignmentEffectiveTo: string | null;
  requestedByUserId: string | null; requestedAt: string | null; approvedByUserId: string | null; approvedAt: string | null;
  currentBalanceHours: number; currentBalanceHoursExact?: string; accrualRate: number; accrualBasis: string; jurisdictionalState: string | null;
  accrualStartDate: string | null; entitlementDate: string | null; entitlement: number | null;
  prorate: number | null; lastAccrualPostedAt: string | null;
  heldAvgOrdinaryHoursPerWeek: number | null; heldAvgOrdinaryRatePerWeek: number | null;
  heldAvgAsAtPayEndDate: string | null; effectiveStartDate: string; effectiveEndDate: string | null;
  accountStatus: string; closedAt: string | null; rowVersion: number;
  accountRowVersion: number; ruleRowVersion: number; forecastHourlySupported?: boolean;
  createdAt: string; createdBy: string | null; updatedAt: string; updatedBy: string | null;
  canRemove: boolean; canEnd: boolean; removalBlockedReason: string | null;
  opening?: WorkerLeaveOpeningStatus | null;
}
export interface WorkerLeaveOpeningStatus {
  canOpen: boolean; canCorrect: boolean; ineligibleReason: string | null;
  currentMovementId: string | null; currentOpeningHours: number | null; currentOpeningHoursExact?: string | null;
  balanceAsOfDate: string | null; accrualCoveredThroughDate: string | null;
  reason: string | null; sourceReference: string | null; postedAt: string | null; postedBy: string | null;
}
export interface WorkerLeaveOpeningInput {
  openingBalanceHours: string; balanceAsOfDate: string; accrualCoveredThroughDate: string;
  reason: string; sourceReference: string; operationReference: string; expectedAccountRowVersion: number;
}
export interface WorkerLeaveCorrectionInput {
  openingMovementId: string; replacementBalanceHours: string; balanceAsOfDate: string;
  accrualCoveredThroughDate: string; reason: string; sourceReference: string;
  operationReference: string; expectedAccountRowVersion: number;
}
export interface WorkerLeaveRuleOption {
  id: string; leaveTypeCode: string; leaveTypeName: string; ruleName: string; ruleCode: string;
  versionNumber: number; rowVersion: number; jurisdictionCode: string | null; ruleSource: string;
  effectiveFrom: string | null; effectiveTo: string | null;
  configuredAccrualRate: number | null; eligible: boolean; ineligibleReason: string | null;
}
export interface WorkerLeaveMovement {
  id: string; movementType: string; quantityHours: number; quantityHoursExact?: string; effectiveDate: string; postedAt: string;
  sourceSystem: string; sourceReference: string; sourceRevision: string | null;
  sourceSnapshotDigest: string | null; payRunId: string | null; payRunBatchId: string | null;
  payRunTransactionId: string | null; idempotencyKey: string; reversalOfMovementId: string | null;
  balanceAfterHours: number | null; balanceAfterHoursExact?: string | null; createdAt: string; createdBy: string;
  correctionOfMovementId?: string | null; operationReference?: string | null; reason?: string | null;
  balanceAsOfDate?: string | null; accrualCoveredThroughDate?: string | null;
}
export const exactLeaveHours = (exact: string | null | undefined, legacy: number | null | undefined): string | null =>
  exact ?? (legacy === null || legacy === undefined ? null : legacy.toFixed(4));
export interface WorkerLeavePage<T> { items: T[]; page: number; pageSize: number; totalItems: number; totalPages: number }
export interface WorkerLeaveForecastRequest {
  forecastToDate: string; expectedContributingOrdinaryHours?: number;
  expectedAccountRowVersion: number; expectedRuleRowVersion: number; expectedAssignmentRowVersion: number;
}
export interface WorkerLeaveForecast {
  status: 'calculated' | 'unsupported' | 'incomplete' | 'blocked'; fromDate: string; forecastToDate: string;
  startingStoredBalanceHours: number; expectedContributingOrdinaryHours: number | null; expectedHoursSource: string;
  projectedAnnualAccrualHours: number | null; projectedPersonalAccrualHours?: number | null;
  estimatedClosingBalanceHours: number | null;
  futureConsumptionHours: number | null; futureConsumptionStatus: string; balanceCoverageStatus: string;
  leaveTypeCode: string; leaveTypeName: string; ruleId: string; ruleCode: string; ruleVersion: number;
  ruleRowVersion: number; ruleEffectiveFrom: string; ruleEffectiveTo: string | null;
  accrualRate: number; formulaIdentity: string; calculationContractVersion: string;
  accountRowVersion: number; accountUpdatedAt: string; issues: string[]; warnings: string[];
}

interface Envelope<T> {
  success: boolean;
  statusCode: number;
  result?: T;
  message?: string;
  errorCode?: string;
}

export interface Result<T> { ok: boolean; data?: T; status: number; message?: string; code?: string }

const base = () => `${getApiBaseUrl()}/api/workers`;

const request = async <T>(path: string, init?: RequestInit): Promise<Result<T>> => {
  const epoch = userStore.authEpoch;
  const host = window.location.host;
  try {
    const response = await authFetch(`${base()}${path}`, init);
    const envelope = response.status === 204
      ? { success: true, statusCode: 204 } as Envelope<T>
      : await response.json() as Envelope<T>;
    if (userStore.authEpoch !== epoch || window.location.host !== host)
      return { ok: false, status: 0, message: 'Authentication or tenant changed. Reload this worker.' };
    return {
      ok: response.ok && envelope.success,
      data: envelope.result,
      status: response.status,
      message: envelope.message,
      code: envelope.errorCode,
    };
  } catch {
    return { ok: false, status: 0, message: 'The service is unavailable. Try again.' };
  }
};

const put = (body: unknown): RequestInit => ({
  method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
});

const pendingPages = new Map<string, Promise<Result<HrWorkersPage>>>();
const pendingPeople = new Map<string, Promise<Result<HrWorkerPersonal>>>();

// StrictMode can replay an effect while the first request is in flight. Share that
// request within the current JS runtime; settled responses are never persisted.
export const getWorkersPage = (page: number): Promise<Result<HrWorkersPage>> => {
  const key = `${window.location.host}:${userStore.authEpoch}:${page}`;
  const existing = pendingPages.get(key);
  if (existing) return existing;
  const pending = request<HrWorkersPage>(`?page=${page}&limit=50`).finally(() => pendingPages.delete(key));
  pendingPages.set(key, pending);
  return pending;
};
export const getHrWorker = (personId: string): Promise<Result<HrWorkerPersonal>> => {
  const key = `${window.location.host}:${userStore.authEpoch}:${personId}`;
  const existing = pendingPeople.get(key);
  if (existing) return existing;
  const pending = request<HrWorkerPersonal>(`/${encodeURIComponent(personId)}`).finally(() => pendingPeople.delete(key));
  pendingPeople.set(key, pending);
  return pending;
};
export const getWorkerAssignments = (personId: string) => request<WorkerAssignments>(`/${encodeURIComponent(personId)}/assignments`);
export const getWorkerPayrollDetails = (personId: string) => request<WorkerPayrollDetails>(`/${encodeURIComponent(personId)}/payroll-details`);
export const saveEmployment = (personId: string, businessRecordId: string, payGroupId: string, clearInvalidSuperannuation = false) =>
  request<WorkerAssignments>(`/${encodeURIComponent(personId)}/employment`, put({ businessRecordId, payGroupId, clearInvalidSuperannuation }));
export const saveSuperannuation = (personId: string, superSchemeId: string) =>
  request<WorkerAssignments>(`/${encodeURIComponent(personId)}/superannuation`, put({ superSchemeId }));
export const getBusinessRecords = () => request<WorkerReference[]>('/references/business-records');
export const getPayGroups = (businessRecordId: string) => request<WorkerReference[]>(`/references/business-records/${encodeURIComponent(businessRecordId)}/pay-groups`);
export const getSuperSchemes = (businessRecordId: string) => request<WorkerReference[]>(`/references/business-records/${encodeURIComponent(businessRecordId)}/super-schemes`);
const leavePath = (personId: string) => `/${encodeURIComponent(personId)}/leave`;
export const getWorkerLeaveAccounts = (personId: string) => request<WorkerLeaveAccount[]>(leavePath(personId));
export const forecastWorkerLeave = (personId: string, accountId: string, forecast: WorkerLeaveForecastRequest) =>
  request<WorkerLeaveForecast>(`${leavePath(personId)}/${encodeURIComponent(accountId)}/forecast`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...forecast, personalUiCompatible: true }),
  });
export const getWorkerLeaveRules = (personId: string, search: string, page: number) =>
  request<WorkerLeavePage<WorkerLeaveRuleOption>>(`${leavePath(personId)}/eligible-rules?search=${encodeURIComponent(search)}&page=${page}&pageSize=25`);
export const getWorkerLeaveMovements = (personId: string, accountId: string, page: number) =>
  request<WorkerLeavePage<WorkerLeaveMovement>>(`${leavePath(personId)}/${encodeURIComponent(accountId)}/movements?page=${page}&pageSize=25`);
export const postWorkerLeaveOpening = (personId: string, accountId: string, input: WorkerLeaveOpeningInput) =>
  request<WorkerLeaveAccount>(`${leavePath(personId)}/${encodeURIComponent(accountId)}/opening`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  });
export const correctWorkerLeaveOpening = (personId: string, accountId: string, input: WorkerLeaveCorrectionInput) =>
  request<WorkerLeaveAccount>(`${leavePath(personId)}/${encodeURIComponent(accountId)}/opening/corrections`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  });
export const linkWorkerLeaveRule = (personId: string, leaveRuleId: string, expectedRuleRowVersion: number) =>
  request<WorkerLeaveAccount>(`${leavePath(personId)}/rule-assignments`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ leaveRuleId, expectedRuleRowVersion }),
  });
export const removeWorkerLeaveRule = (personId: string, assignmentId: string, rowVersion: number) =>
  request<WorkerLeaveAccount>(`${leavePath(personId)}/rule-assignments/${encodeURIComponent(assignmentId)}?expectedRowVersion=${rowVersion}`, { method: 'DELETE' });
export const endWorkerLeaveRule = (personId: string, assignmentId: string, rowVersion: number) =>
  request<WorkerLeaveAccount>(`${leavePath(personId)}/rule-assignments/${encodeURIComponent(assignmentId)}/end?expectedRowVersion=${rowVersion}`, { method: 'POST' });
